import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

const NamePickerDetail = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily } = useFamily();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [drawnName, setDrawnName] = useState(null);
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] = useState(false);
  const [editParticipantDialogOpen, setEditParticipantDialogOpen] = useState(false);
  const [deleteParticipantDialogOpen, setDeleteParticipantDialogOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [participantFormData, setParticipantFormData] = useState({
    name: '',
    email: '',
  });
  const [joinFormData, setJoinFormData] = useState({
    email: '',
  });

  const fetchGroup = useCallback(async () => {
    try {
      let response;
      if (token) {
        response = await api.get(`/name-picker/join/${token}/`);
        setGroup(response.data);
        // Check if user is already in the group
        const isParticipant = response.data.participants?.some(
          (p) => p.user_id === user?.uid
        );
        if (!isParticipant && user) {
          setJoinDialogOpen(true);
        }
      } else {
        response = await api.get(`/name-picker/${id}/`);
        setGroup(response.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [id, token, user]);

  const handleJoin = async () => {
    try {
      const endpoint = token
        ? `/name-picker/join/${token}/`
        : `/name-picker/${id}/join/`;
      await api.post(endpoint, joinFormData);
      setJoinDialogOpen(false);
      fetchGroup();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to join group');
    }
  };

  const handleDrawNames = async () => {
    try {
      const response = await api.post(`/name-picker/${id}/draw_names/`);
      setGroup(response.data);
      alert('Names have been drawn!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to draw names');
    }
  };

  const handleViewDrawnName = async () => {
    try {
      const participant = group.participants.find((p) => p.user_id === user.uid);
      if (!participant) return;

      const response = await api.post(
        `/name-picker/${id}/participants/${participant.id}/view/`
      );
      setDrawnName(response.data.drawn_name);
      setViewDialogOpen(true);
      fetchGroup();
    } catch (error) {
      alert('Failed to view drawn name');
    }
  };

  const copyShareLink = () => {
    const url = token
      ? window.location.href
      : `${window.location.origin}/name-picker/join/${group.share_token}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };


  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container>
        <Typography>Group not found</Typography>
      </Container>
    );
  }

  const isCreator = user && user.uid === group.created_by;
  const userMember = currentFamily?.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
  const canManage = isCreator || isAdmin;
  const participant = group.participants?.find((p) => p.user_id === user?.uid);
  const canViewName = participant && group.is_drawn && !participant.has_viewed;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {!token && (
        <Button onClick={() => navigate('/name-picker')} sx={{ mb: 2 }}>
          ← Back to Name Picker
        </Button>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box>
              <Typography variant="h4" gutterBottom>
                {group.name}
              </Typography>
              {group.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {group.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {group.participant_count} participants
              </Typography>
              {group.is_drawn && (
                <Chip
                  label="Names Drawn"
                  color="success"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            <Tooltip title="Copy share link">
              <IconButton onClick={copyShareLink}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {canViewName && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have a name to view! Click the button below to see who you drew.
        </Alert>
      )}

      {participant && group.is_drawn && participant.has_viewed && (
        <Alert severity="success" sx={{ mb: 2 }}>
          You've already viewed your drawn name: <strong>{participant.drawn_name}</strong>
        </Alert>
      )}

      <Box display="flex" gap={2} mb={2}>
        {isCreator && !group.is_drawn && (
          <Button
            variant="contained"
            onClick={handleDrawNames}
            disabled={group.participant_count < 2}
          >
            Draw Names
          </Button>
        )}
      {canViewName && (
        <Button variant="contained" color="primary" onClick={handleViewDrawnName}>
          View My Drawn Name
        </Button>
      )}
      </Box>

      <>
        {/* Show results cards if drawn */}
          {group.is_drawn && group.participants && group.participants.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Name Picker Results
              </Typography>
              {group.draw_date_display && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Drawn on: {group.draw_date_display}
                </Typography>
              )}
              <Grid container spacing={2}>
                {group.participants.map((p) => (
                  <Grid item xs={12} sm={6} md={4} key={p.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom>
                              {p.name}
                            </Typography>
                            <Typography variant="body1" color="primary">
                              → {p.drawn_name || 'Not assigned'}
                            </Typography>
                          </Box>
                          {canManage && (
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingParticipant(p);
                                  setParticipantFormData({ name: p.name, email: p.email || '' });
                                  setEditParticipantDialogOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setEditingParticipant(p);
                                  setDeleteParticipantDialogOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Participants list */}
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Participants
                </Typography>
                {canManage && !group.is_drawn && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setParticipantFormData({ name: '', email: '' });
                      setAddParticipantDialogOpen(true);
                    }}
                    size="small"
                  >
                    Add Name
                  </Button>
                )}
              </Box>
              {group.participants?.length === 0 ? (
                <Typography>No participants yet. {canManage ? 'Add names to get started!' : 'Share the link to invite people!'}</Typography>
              ) : (
                <List>
                  {group.participants.map((p) => (
                    <ListItem 
                      key={p.id}
                      secondaryAction={
                        canManage && (
                          <Box>
                            <IconButton
                              edge="end"
                              onClick={() => {
                                setEditingParticipant(p);
                                setParticipantFormData({ name: p.name, email: p.email || '' });
                                setEditParticipantDialogOpen(true);
                              }}
                              size="small"
                              title="Edit name"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => {
                                setEditingParticipant(p);
                                setDeleteParticipantDialogOpen(true);
                              }}
                              size="small"
                              color="error"
                              title="Delete name"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )
                      }
                    >
                      <ListItemText
                        primary={p.name}
                        secondary={
                          group.is_drawn && p.has_viewed
                            ? `Viewed their name`
                            : group.is_drawn
                            ? 'Has not viewed yet'
                            : 'Waiting for draw'
                        }
                      />
                      {p.user_id === user?.uid && (
                        <Chip label="You" size="small" sx={{ ml: 1 }} />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
      </>

      <Dialog
        open={joinDialogOpen}
        onClose={() => setJoinDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Join Name Picker Group</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email (optional)"
            margin="normal"
            type="email"
            value={joinFormData.email}
            onChange={(e) =>
              setJoinFormData({ ...joinFormData, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleJoin} variant="contained">
            Join
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>🎉 Your Drawn Name</DialogTitle>
        <DialogContent>
          <Typography variant="h4" align="center" sx={{ py: 4 }}>
            {drawnName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog
        open={addParticipantDialogOpen}
        onClose={() => setAddParticipantDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Name</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={participantFormData.name}
            onChange={(e) =>
              setParticipantFormData({ ...participantFormData, name: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Email (optional)"
            margin="normal"
            type="email"
            value={participantFormData.email}
            onChange={(e) =>
              setParticipantFormData({ ...participantFormData, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddParticipantDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                await api.post(`/name-picker/${id}/participants/`, participantFormData);
                setAddParticipantDialogOpen(false);
                setParticipantFormData({ name: '', email: '' });
                fetchGroup();
              } catch (error) {
                alert(error.response?.data?.error || 'Failed to add participant');
              }
            }}
            variant="contained"
            disabled={!participantFormData.name}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Participant Dialog */}
      <Dialog
        open={editParticipantDialogOpen}
        onClose={() => {
          setEditParticipantDialogOpen(false);
          setEditingParticipant(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Name</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={participantFormData.name}
            onChange={(e) =>
              setParticipantFormData({ ...participantFormData, name: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            label="Email (optional)"
            margin="normal"
            type="email"
            value={participantFormData.email}
            onChange={(e) =>
              setParticipantFormData({ ...participantFormData, email: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditParticipantDialogOpen(false);
              setEditingParticipant(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await api.put(`/name-picker/${id}/participants/${editingParticipant.id}/`, participantFormData);
                setEditParticipantDialogOpen(false);
                setEditingParticipant(null);
                setParticipantFormData({ name: '', email: '' });
                fetchGroup();
                if (group.is_drawn) {
                  alert('Name updated. Note: If names have already been drawn, you may need to redraw to update the pairings.');
                }
              } catch (error) {
                alert(error.response?.data?.error || 'Failed to update participant');
              }
            }}
            variant="contained"
            disabled={!participantFormData.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Participant Dialog */}
      <Dialog
        open={deleteParticipantDialogOpen}
        onClose={() => {
          setDeleteParticipantDialogOpen(false);
          setEditingParticipant(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Name</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{editingParticipant?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteParticipantDialogOpen(false);
              setEditingParticipant(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await api.delete(`/name-picker/${id}/participants/${editingParticipant.id}/`);
                setDeleteParticipantDialogOpen(false);
                setEditingParticipant(null);
                fetchGroup();
                if (group.is_drawn) {
                  alert('Name deleted. Note: If names have already been drawn, you may need to redraw to update the pairings.');
                }
              } catch (error) {
                alert(error.response?.data?.error || 'Failed to delete participant');
              }
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NamePickerDetail;
